import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Search, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Input,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@nexus/ui";
import { promptUsedInValuesQueryOptions } from "@/lib/queries";
import type { PromptsListFilters } from "@/lib/queries";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { callSiteSuffix, groupCallSites } from "@/features/prompts/lib/prompt-list";

const ALL = "__all__";
const GLOBAL = "__global__";

const TASK_LABELS = {
  TEXT: { key: "prompts.table.taskText", fallback: "Text" },
  VISION: { key: "prompts.table.taskVision", fallback: "Vision" },
  IMAGE: { key: "prompts.table.taskImage", fallback: "Image" },
} as const;

interface TenantFilterOption {
  id: string;
  name: string;
}

interface UsedInOption {
  /** The full call-site key, or `ALL`. */
  value: string;
  /** Row text — the suffix, since the family heading carries the prefix. */
  label: string;
}

interface PromptsTableToolbarProps {
  filters: PromptsListFilters;
  onFiltersChange: (filters: PromptsListFilters) => void;
  search: string;
  onSearchChange: (search: string) => void;
  visibleCount: number;
  totalCount: number;
  onCreateNew: () => void;
}

export function PromptsTableToolbar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  visibleCount,
  totalCount,
  onCreateNew,
}: PromptsTableToolbarProps) {
  const { t } = useTranslation();
  const { data: usedInValues = [] } = useQuery(promptUsedInValuesQueryOptions());
  const tenants = useAuth((s) => s.tenants);

  const usedInGroups = useMemo(
    () => [
      {
        items: [
          { value: ALL, label: t("prompts.toolbar.optionEverywhere", "Everywhere") },
        ],
      },
      ...groupCallSites(usedInValues).map((group) => ({
        heading: group.family ?? t("prompts.table.groupOther", "Other"),
        items: group.callSites.map((callSite) => ({
          value: callSite,
          label: callSiteSuffix(callSite, group.family),
        })),
      })),
    ],
    [usedInValues, t],
  );

  const tenantGroups = useMemo(() => {
    const permitted = tenants
      .filter((tenant) =>
        tenant.permissions.some((p) => p.type === "PAGE" && p.route === "prompts"),
      )
      .map((tenant) => ({ id: tenant.id, name: tenant.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [
      {
        items: [
          { id: ALL, name: t("prompts.toolbar.optionAll", "All") },
          { id: GLOBAL, name: t("prompts.toolbar.tenantGlobal", "Global") },
        ],
      },
      { items: permitted },
    ];
  }, [tenants, t]);

  const selectedTenantName = useMemo(() => {
    if (!filters.tenantId) return t("prompts.toolbar.optionAll", "All");
    if (filters.tenantId === GLOBAL) return t("prompts.toolbar.tenantGlobal", "Global");
    return (
      tenants.find((tenant) => tenant.id === filters.tenantId)?.name ??
      t("prompts.toolbar.optionAll", "All")
    );
  }, [filters.tenantId, tenants, t]);

  const hasActiveFilters = Boolean(
    filters.usedIn || filters.tenantId || filters.status || filters.taskType || search,
  );

  const handleClear = () => {
    onFiltersChange({});
    onSearchChange("");
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-4">
      <Button size="sm" onClick={onCreateNew} className="gap-1.5">
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {t("prompts.toolbar.newPrompt", "New Prompt")}
      </Button>

      <div className="relative w-[260px]">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          aria-label={t("prompts.toolbar.searchAria", "Search prompts")}
          placeholder={t("prompts.toolbar.search", "Search name, call site, template…")}
          className="h-8 pl-8 text-xs"
        />
      </div>

      <SearchableCombobox<UsedInOption>
        groups={usedInGroups}
        getKey={(option) => option.value}
        getLabel={(option) =>
          t("prompts.toolbar.usedInValue", "Used in: {{value}}", {
            value:
              option.value === ALL
                ? t("prompts.toolbar.optionEverywhere", "Everywhere")
                : option.value,
          })
        }
        // Search the whole key, not the shortened row label, so typing a family
        // name still reaches every call site inside it.
        getSearchText={(option) => option.value}
        renderItem={(option) => (
          <span
            className={cn("min-w-0 flex-1 truncate", option.value !== ALL && "font-mono")}
          >
            {option.label}
          </span>
        )}
        value={filters.usedIn ?? ALL}
        onSelect={(option) =>
          onFiltersChange({
            ...filters,
            usedIn: option.value === ALL ? undefined : option.value,
          })
        }
        ariaLabel={t("prompts.toolbar.usedInAria", "Filter by call site")}
        searchPlaceholder={t("prompts.toolbar.usedInSearch", "Search call sites…")}
        triggerClassName="h-8 w-[220px] text-xs"
        contentClassName="min-w-[320px]"
        // Mono runs visually larger than the sans rows in the neighbouring
        // filters, so it needs a step down to read as the same size.
        itemClassName="text-xs"
        closeOnSelect
      />

      <SearchableCombobox<TenantFilterOption>
        groups={tenantGroups}
        getKey={(tenant) => tenant.id}
        getLabel={(tenant) =>
          t("prompts.toolbar.tenantValue", "Tenant: {{name}}", { name: tenant.name })
        }
        getSearchText={(tenant) => tenant.name}
        renderItem={(tenant) => <span className="truncate">{tenant.name}</span>}
        value={filters.tenantId ?? ALL}
        onSelect={(tenant) =>
          onFiltersChange({
            ...filters,
            tenantId: tenant.id === ALL ? undefined : tenant.id,
          })
        }
        placeholder={t("prompts.toolbar.tenantValue", "Tenant: {{name}}", {
          name: selectedTenantName,
        })}
        searchPlaceholder={t("prompts.toolbar.tenantSearch", "Search tenants…")}
        triggerClassName="h-8 w-[180px] text-xs"
        closeOnSelect
      />

      <Select
        value={filters.taskType ?? ALL}
        onValueChange={(val) =>
          onFiltersChange({
            ...filters,
            taskType: val === ALL ? undefined : (val as PromptsListFilters["taskType"]),
          })
        }
      >
        <SelectTrigger className="h-8 w-[140px] text-xs">
          <SelectValue>
            {t("prompts.toolbar.taskValue", "Task: {{value}}", {
              value: filters.taskType
                ? t(TASK_LABELS[filters.taskType].key, TASK_LABELS[filters.taskType].fallback)
                : t("prompts.toolbar.optionAll", "All"),
            })}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("prompts.toolbar.optionAll", "All")}</SelectItem>
          <SelectItem value="TEXT">{t("prompts.table.taskText", "Text")}</SelectItem>
          <SelectItem value="VISION">{t("prompts.table.taskVision", "Vision")}</SelectItem>
          <SelectItem value="IMAGE">{t("prompts.table.taskImage", "Image")}</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? ALL}
        onValueChange={(val) =>
          onFiltersChange({ ...filters, status: val === ALL ? undefined : val })
        }
      >
        <SelectTrigger className="h-8 w-[140px] text-xs">
          <SelectValue>
            {t("prompts.toolbar.statusValue", "Status: {{value}}", {
              value:
                filters.status === "ACTIVE"
                  ? t("prompts.status.active", "Active")
                  : filters.status === "INACTIVE"
                    ? t("prompts.status.inactive", "Inactive")
                    : t("prompts.toolbar.optionAll", "All"),
            })}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("prompts.toolbar.optionAll", "All")}</SelectItem>
          <SelectItem value="ACTIVE">{t("prompts.status.active", "Active")}</SelectItem>
          <SelectItem value="INACTIVE">
            {t("prompts.status.inactive", "Inactive")}
          </SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-xs"
          onClick={handleClear}
        >
          <X className="size-3.5" aria-hidden />
          {t("common.clearFilters", "Clear filters")}
        </Button>
      )}

      <p className="ml-auto shrink-0 text-[12px] tabular-nums text-muted-foreground">
        {visibleCount === totalCount
          ? t("prompts.toolbar.countAll", "{{count}} prompts", { count: totalCount })
          : t("prompts.toolbar.countFiltered", "{{visible}} of {{total}}", {
              visible: visibleCount,
              total: totalCount,
            })}
      </p>
    </div>
  );
}
